import React, { FC } from 'react'
import Box from 'transfix-ui/core/Box'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { AnnualVolumeInput } from 'apps/marketplace/components/rfp/StrategyManager/inputs/AnnualVolumeInput'
import { ContractDurationInput } from 'apps/marketplace/components/rfp/StrategyManager/inputs/ContractDurationInput'
import { DatVarianceInput } from 'apps/marketplace/components/rfp/StrategyManager/inputs/DatVarianceInput'
import { EffectiveMileageInput } from 'apps/marketplace/components/rfp/StrategyManager/inputs/EffectiveMileageInput'
import { EquipmentInput } from 'apps/marketplace/components/rfp/StrategyManager/inputs/EquipmentInput'
import { ExtraStopsInput } from 'apps/marketplace/components/rfp/StrategyManager/inputs/ExtraStopsInput'
import { MarketCenterDistanceInput } from 'apps/marketplace/components/rfp/StrategyManager/inputs/MarketCenterDistanceInput'
import { MarketDemandScoreInput } from 'apps/marketplace/components/rfp/StrategyManager/inputs/MarketDemandScoreInput'
import { MinimumMarginInput } from 'apps/marketplace/components/rfp/StrategyManager/inputs/MinimumMarginInput'
import { NegativeMarginRiskInput } from 'apps/marketplace/components/rfp/StrategyManager/inputs/NegativeMarginRiskInput'
import { RiskyAuditLevelInput } from 'apps/marketplace/components/rfp/StrategyManager/inputs/RiskyAuditLevelInput'
import {
  EditCategories,
  EligibilityCriteriaFields,
  ThresholdManagementFields,
  ILaneCriteria,
  ICriteriaCondition,
  MarketCenterDistanceTypes,
  Strategy,
} from 'apps/marketplace/components/rfp/types/strategyManager'
import {
  getEquipmentValue,
  getRiskyAuditValues,
  getContractDurationValue,
  getPuMarketCenterDistance,
  getDelMarketCenterDistance,
  getAnnualVolume,
  getMarketDemandScore,
  getExtraStops,
  getEffectiveMileage,
  getDatVariance,
  getNegativeMarginRisk,
  getMinimumRate,
  getLengthOfHaul,
} from 'apps/marketplace/components/rfp/utils/strategyManager'

interface IEditableFieldInputProps {
  currentField: EligibilityCriteriaFields | ThresholdManagementFields | null
  currentLaneCriteria: ILaneCriteria | null | undefined
  currentStrategy: Strategy | null | undefined
  currentCategory: EditCategories | null
  onCancel: () => void
  onLaneCriteriaChange: (changes: Partial<ILaneCriteria>) => void
  onStrategyChange: (changes: Strategy) => void
}

const useStyles = makeStyles({
  heading: {
    fontSize: '1.2rem',
  },
})

export const EditableFieldInput: FC<IEditableFieldInputProps> = ({
  currentCategory,
  currentField,
  currentLaneCriteria,
  currentStrategy,
  onCancel,
  onLaneCriteriaChange,
  onStrategyChange,
}) => {
  const classes = useStyles()

  const onLaneCriteriaSave = (changes: Partial<ILaneCriteria>) => {
    onLaneCriteriaChange(changes)
    onCancel()
  }

  const onVarianceFlagSave = (flagName: string, value: ICriteriaCondition) => {
    onStrategyChange({
      ...currentStrategy,
      strategyMatrix: {
        ...currentStrategy?.strategyMatrix,
        varianceFlags: {
          ...currentStrategy?.strategyMatrix?.varianceFlags,
          [flagName]: value,
        },
      },
    })
    onCancel()
  }

  const onMinimumMarginSave = (minimumRate: string, lengthOfHaul: string) => {
    onStrategyChange({
      ...currentStrategy,
      strategyMatrix: {
        ...currentStrategy?.strategyMatrix,
        lengthOfHaul,
        minimumRate,
      },
    })
    onCancel()
  }

  return (
    <Box>
      <Box mb={1}>
        <Typography variant='h6' color='textPrimary' className={classes.heading}>
          {currentCategory === EditCategories.eligibilityCriteria && <>Edit Eligibility Criteria</>}
          {currentCategory === EditCategories.thresholdManagement && <>Edit Threshold Management</>}
        </Typography>
      </Box>
      <Box mb={2}>
        {currentField === EligibilityCriteriaFields.equipment && (
          <EquipmentInput
            value={getEquipmentValue(currentLaneCriteria)}
            onSave={onLaneCriteriaSave}
            onCancel={onCancel}
          />
        )}
        {currentField === EligibilityCriteriaFields.riskyAuditLevel && (
          <RiskyAuditLevelInput
            value={getRiskyAuditValues(currentLaneCriteria)}
            onSave={onLaneCriteriaSave}
            onCancel={onCancel}
          />
        )}
        {currentField === EligibilityCriteriaFields.contractDuration && (
          <ContractDurationInput
            value={getContractDurationValue(currentLaneCriteria)}
            onSave={onLaneCriteriaSave}
            onCancel={onCancel}
          />
        )}
        {currentField === EligibilityCriteriaFields.pickupMarketCenterDistance && (
          <MarketCenterDistanceInput
            type={MarketCenterDistanceTypes.pickup}
            value={getPuMarketCenterDistance(currentLaneCriteria)}
            onSave={onLaneCriteriaSave}
            onCancel={onCancel}
          />
        )}
        {currentField === EligibilityCriteriaFields.deliveryMarketCenterDistance && (
          <MarketCenterDistanceInput
            type={MarketCenterDistanceTypes.delivery}
            value={getDelMarketCenterDistance(currentLaneCriteria)}
            onSave={onLaneCriteriaSave}
            onCancel={onCancel}
          />
        )}
        {currentField === EligibilityCriteriaFields.annualVolume && (
          <AnnualVolumeInput
            value={getAnnualVolume(currentLaneCriteria)}
            onSave={onLaneCriteriaSave}
            onCancel={onCancel}
          />
        )}
        {currentField === EligibilityCriteriaFields.marketDemandScore && (
          <MarketDemandScoreInput
            value={getMarketDemandScore(currentLaneCriteria)}
            onSave={onLaneCriteriaSave}
            onCancel={onCancel}
          />
        )}
        {currentField === EligibilityCriteriaFields.extraStops && (
          <ExtraStopsInput
            value={getExtraStops(currentLaneCriteria)}
            onSave={onLaneCriteriaSave}
            onCancel={onCancel}
          />
        )}
        {currentField === EligibilityCriteriaFields.effectiveMileage && (
          <EffectiveMileageInput
            value={getEffectiveMileage(currentLaneCriteria)}
            onSave={onLaneCriteriaSave}
            onCancel={onCancel}
          />
        )}
        {currentField === ThresholdManagementFields.datVariance && (
          <DatVarianceInput
            value={getDatVariance(currentStrategy)}
            onSave={onVarianceFlagSave}
            onCancel={onCancel}
          />
        )}
        {currentField === ThresholdManagementFields.negativeMarginRisk && (
          <NegativeMarginRiskInput
            value={getNegativeMarginRisk(currentStrategy)}
            onSave={onVarianceFlagSave}
            onCancel={onCancel}
          />
        )}
        {currentField === ThresholdManagementFields.minimumMargin && (
          <MinimumMarginInput
            minRate={getMinimumRate(currentStrategy) || '0'}
            lengthOfHaul={getLengthOfHaul(currentStrategy) || '0'}
            onSave={onMinimumMarginSave}
            onCancel={onCancel}
          />
        )}
      </Box>
    </Box>
  )
}
